import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  SimpleGrid,
  Input,
  Spinner,
  Box,
  Button,
  Image,
  Center,
  IconButton,
  Icon,
  ButtonGroup,
  Dialog,
  CloseButton,
  Portal,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import {
  XCircleIcon,
  CloudArrowUpIcon,
  PhotoIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { MediaBlock as IMediaBlock, FileType } from '@/lib/types';
import BaseInputBlock from './base-input-block';
import api from '@/utils/api';
import ThumbnailBlock from './thumbnail-block';
import useModalToggle from '@/lib/hooks/use-modal-toggle';

interface Props {
  block: IMediaBlock;
}

const getFileType = (fileUrl: string): FileType => {
  const extension = fileUrl.split('.').pop()?.toLowerCase() || '';

  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv'];
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

  if (videoExtensions.includes(extension)) return FileType.Video;
  if (imageExtensions.includes(extension)) return FileType.Image;

  return FileType.Image;
};

export default function EditMediaBlock({ block }: Props) {
  const { id } = useParams<{
    id: string;
  }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState<string[]>([]);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [currentThumb, setCurrentThumb] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const thumbnailModal = useModalToggle();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await api.get(`contents/${id}`);
      const data = response.data.data;
      const files = data?.media_files?.files || [];
      const thumbnails = data?.media_files?.thumbnails || [];

      setFiles(files);
      setThumbnails(thumbnails);
      setCurrentThumb(data?.thumbnail || '');
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch data',
      });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (!selectedFiles || selectedFiles.length === 0 || !id) return;

      const formData = new FormData();
      formData.append('file', selectedFiles[0]); // Append only the first file

      try {
        setUploading(true);

        if (selectedFiles[0].type.includes('video')) {
          await api.post(`/media/contents/${id}/video`, formData);
        } else {
          await api.post(`/media/contents/${id}/image`, formData);
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = ''; // Reset the file input value
        }

        await fetchData();

        toaster.create({
          title: 'Media uploaded successfully',
          type: 'success',
          duration: 2000,
        });
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to upload media',
        });
        setUploading(false);
      } finally {
        setUploading(false);
      }
    },
    [id, fetchData],
  );

  const handleDeleteFile = useCallback(
    async (file: string) => {
      if (!id) return;

      try {
        setDeletingFile(file);
        await api.put(`/media/contents/${id}`, { file_url: file });

        setFiles((prevFiles) => prevFiles.filter((f) => f !== file));

        toaster.create({
          title: 'Media was removed successfully',
          type: 'success',
          duration: 2000,
        });

        if (file === currentThumb) {
          setCurrentThumb('');
        }
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to delete media',
          duration: 3000,
        });

        fetchData();
      } finally {
        setDeletingFile(null);
        setFileToDelete(null);
        setShowDeleteModal(false);
      }
    },
    [id, currentThumb, fetchData],
  );

  const confirmDelete = (file: string) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const handleCardClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileChange({
          target: { files: e.dataTransfer.files },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    },
    [handleFileChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick],
  );

  const generateThumbnail = async () => {
    if (!id) return;

    setIsGenerating(true);
    try {
      await api.post(`/thumbnails/contents/${id}`);

      toaster.create({
        title: 'Thumbnail generated successfully',
        type: 'success',
        duration: 2000,
      });

      await fetchData();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to generate thumbnail',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <BaseInputBlock block={block}>
      {isLoading ? (
        <Center py={10}>
          <Spinner size='xl' />
        </Center>
      ) : (
        <>
          <SimpleGrid gap={4} columns={{ base: 2, md: 3, lg: 4 }} w={'full'}>
            {files.map((file, index) => (
              <Box
                key={index}
                borderWidth='1px'
                borderRadius='lg'
                borderColor='gray.200'
                overflow='hidden'
                position='relative'
                aria-label={`Media file ${index + 1}`}
                transition='all 0.2s'
                _hover={{ borderColor: 'gray.300', transform: 'translateY(-2px)' }}
              >
                <Center position={'relative'} h='160px'>
                  {getFileType(file) === FileType.Image ? (
                    <>
                      <Image
                        src={file}
                        alt={'Media file'}
                        boxSize={'160px'}
                        objectFit={'cover'}
                        opacity={deletingFile === file ? 0.5 : 1}
                        loading='lazy'
                      />
                    </>
                  ) : (
                    <Box as='video' boxSize={'160px'}>
                      <source src={file} type='video/mp4' />
                      Your browser does not support the video tag.
                    </Box>
                  )}

                  <Tooltip content={'Remove a media'}>
                    <IconButton
                      variant='subtle'
                      size={'xs'}
                      color={'red'}
                      aria-label={'Remove a media'}
                      position={'absolute'}
                      top={0.5}
                      right={0.5}
                      onClick={() => confirmDelete(file)}
                      loading={deletingFile === file}
                    >
                      <Icon as={XCircleIcon} w={5} h={5} />
                    </IconButton>
                  </Tooltip>

                  {deletingFile === file && (
                    <Box
                      position='absolute'
                      inset={0}
                      bg='blackAlpha.300'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                    >
                      <Spinner />
                    </Box>
                  )}
                </Center>
              </Box>
            ))}
          </SimpleGrid>

          <ButtonGroup w={'full'} mt={4} display={'flex'}>
            <Button
              variant='surface'
              size='sm'
              borderColor={dragActive ? 'blue.400' : 'gray.100'}
              borderStyle={dragActive || uploading ? 'dashed' : 'solid'}
              onClick={uploading ? undefined : handleCardClick}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onKeyDown={handleKeyDown}
              loading={uploading}
              loadingText='Uploading...'
              flex={1}
            >
              <Icon as={CloudArrowUpIcon} w={4} h={4} /> Upload media file
              <Input
                ref={fileInputRef}
                id={`block-${block.referenceID}`}
                type={'file'}
                accept={'image/*,video/*'}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                aria-hidden='true'
              />
            </Button>
            {thumbnails.length === 0 ? (
              <Button
                variant='surface'
                size='sm'
                flex={1}
                onClick={generateThumbnail}
                loading={isGenerating}
              >
                <Icon as={ArrowPathIcon} w={4} h={4} />
                Generate thumbnail
              </Button>
            ) : (
              <Button variant='surface' size='sm' flex={1} onClick={thumbnailModal.open}>
                <Icon as={PhotoIcon} w={4} h={4} />
                Thumbnail
              </Button>
            )}
          </ButtonGroup>
        </>
      )}

      {id && thumbnailModal && (
        <Dialog.Root
          open={thumbnailModal.isOpen}
          onOpenChange={thumbnailModal.close}
          motionPreset='slide-in-bottom'
          size='lg'
        >
          <Dialog.Trigger />
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>
                  <Dialog.Title>Thumbnail</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <ThumbnailBlock
                    id={id}
                    thumbnails={thumbnails}
                    currentThumb={currentThumb}
                    setCurrentThumb={setCurrentThumb}
                    reload={() => {
                      fetchData();
                      thumbnailModal.close();
                    }}
                  />
                </Dialog.Body>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size='sm' />
                </Dialog.CloseTrigger>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      )}

      {showDeleteModal && (
        <DestructiveModal
          isOpen={showDeleteModal}
          onCancel={() => setShowDeleteModal(!showDeleteModal)}
          onConfirm={() => fileToDelete && handleDeleteFile(fileToDelete)}
          isLoading={!!deletingFile}
          title={'Confirm Deletion'}
          children={` Are you sure you want to delete this media file? This action cannot be undone.`}
          confirmText={'Confirm'}
        />
      )}
    </BaseInputBlock>
  );
}
